import { ChevronRight } from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

export type ItemLista = {
  nome: string;
  /** Segunda linha curta sob o nome: a origem, a explicação. */
  linha2?: string;
  icone?: LucideIcon;
  /** Número ou palavra curta à direita. Nunca uma frase. */
  valor?: string;
  /** Miniatura, quando a foto é o que identifica o item. */
  foto?: string;
  /** Nó próprio para a miniatura, quando a tela precisa de <CachedImage> em
   *  vez de um <img> simples. Tem precedência sobre `foto` e `icone`. */
  mini?: ReactNode;
  /**
   * Ação secundária na própria linha — favoritar, apagar.
   *
   * Fica FORA do botão da linha: botão dentro de botão não é HTML válido e,
   * pior, o toque cai no alvo errado. O alvo dela é 56px próprio, e a linha
   * inteira continua levando para o destino.
   */
  acao?: { icone: LucideIcon; rotulo: string; ativo?: boolean; onClick: () => void };
  onClick?: () => void;
};

/**
 * Bloco de linhas. O segundo e único outro formato do app, para tudo que não
 * é produto comprado: bônus, atalhos, ajustes, ajuda.
 *
 * Filete de 1px separa as linhas — nunca um cartão por linha flutuando.
 */
export function Lista({ itens }: { itens: ItemLista[] }) {
  return (
    <div style={{ paddingLeft: "var(--gutter)", paddingRight: "var(--gutter)" }}>
      <div className="overflow-hidden border border-border bg-card" style={{ borderRadius: "var(--raio-cartao)" }}>
        {itens.map((it, i) => (
          <div
            key={`${i}-${it.nome}`}
            className={`flex items-center ${i > 0 ? "border-t border-border" : ""}`}
          >
          <button
            type="button"
            onClick={it.onClick}
            className="flex min-w-0 flex-1 items-center gap-4 px-5 text-left active:bg-secondary"
            style={{ minHeight: "var(--alvo)", transition: "background-color 120ms" }}
          >
            {it.mini ? (
              <span
                className="shrink-0 overflow-hidden rounded-lg"
                style={{ width: "var(--foto-linha)", height: "var(--foto-linha)" }}
              >
                {it.mini}
              </span>
            ) : it.foto ? (
              <img
                src={it.foto}
                alt=""
                loading="lazy"
                className="shrink-0 rounded-lg object-cover"
                style={{ width: "var(--foto-linha)", height: "var(--foto-linha)" }}
              />
            ) : (
              it.icone && (
                /* O ícone mora num círculo de ouro translúcido: é o que faz
                   uma linha de bônus ou de atalho parecer conteúdo, não um
                   item de ajustes. */
                <span
                  className="flex shrink-0 items-center justify-center rounded-full"
                  style={{ width: 44, height: 44, background: "hsl(var(--accent) / 0.14)", color: "hsl(var(--accent))" }}
                >
                  <it.icone className="h-6 w-6" strokeWidth={2.2} />
                </span>
              )
            )}
            <span className="min-w-0 flex-1">
              <span className="block font-semibold leading-tight" style={{ fontSize: "var(--t-linha)" }}>
                {it.nome}
              </span>
              {it.linha2 && (
                <span className="block pt-1 leading-snug" style={{ fontSize: "var(--t-etiqueta)", color: "var(--tinta-apoio)" }}>
                  {it.linha2}
                </span>
              )}
            </span>
            {it.valor && (
              <span style={{ fontSize: "var(--t-apoio)", color: "var(--tinta-apoio)" }}>
                {it.valor}
              </span>
            )}
            <ChevronRight className="h-7 w-7 shrink-0" strokeWidth={2.5} style={{ color: "var(--tinta-fraca)" }} />
          </button>
          {it.acao && (
            <button
              type="button"
              aria-label={it.acao.rotulo}
              aria-pressed={it.acao.ativo}
              onClick={it.acao.onClick}
              className="flex shrink-0 items-center justify-center border-l border-border"
              style={{ width: "var(--alvo-min)", height: "var(--alvo)" }}
            >
              <it.acao.icone
                className="h-6 w-6"
                strokeWidth={it.acao.ativo ? 2.6 : 2}
                style={{ color: it.acao.ativo ? "hsl(var(--accent))" : "var(--tinta-apoio)" }}
                fill={it.acao.ativo ? "currentColor" : "none"}
              />
            </button>
          )}
          </div>
        ))}
      </div>
    </div>
  );
}
